// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import {
  MessageSquare,
  X,
  Send,
  Minimize2,
  Maximize2,
  Bot,
  User,
  Loader,
  AlertCircle,
  RefreshCw,
  Search,
  Bookmark,
  Copy,
  Phone,
  FileText,
  Users,
  Settings,
  HelpCircle
} from 'lucide-react';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isLoading?: boolean;
  category?: 'general' | 'client_lookup' | 'process_help' | 'escalation' | 'knowledge_base';
  clientId?: string;
  tags?: string[];
}

interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: () => void;
  category: string;
}

interface CustomerServiceChatbotProps {
  userRole?: 'customer_service' | 'supervisor' | 'manager';
  userName?: string;
  userDepartment?: string;
  onClientLookup?: (clientId: string) => void;
  onEscalation?: (issue: any) => void;
  onTransfer?: (department: string) => void;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
}

const CustomerServiceChatbot: React.FC<CustomerServiceChatbotProps> = ({
  userRole = 'customer_service',
  userName = 'CS Agent',
  userDepartment = 'Customer Service',
  onClientLookup,
  onEscalation,
  onTransfer,
  position = 'bottom-right'
}) => {
  const isComingSoon = true;
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentCategory, setCurrentCategory] = useState<string>('general');
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [bookmarkedResponses, setBookmarkedResponses] = useState<ChatMessage[]>([]);
  const [activeTickets, setActiveTickets] = useState<any[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  // Initialize with welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = getWelcomeMessage();
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: welcomeMessage,
        timestamp: new Date(),
        category: 'general'
      }]);

      // Load mock active tickets
      setActiveTickets([
        { id: 'CS-001', client: 'John Smith', priority: 'high', issue: 'Credit report dispute' },
        { id: 'CS-002', client: 'Maria Garcia', priority: 'medium', issue: 'Account access' },
        { id: 'CS-003', client: 'Robert Johnson', priority: 'low', issue: 'General inquiry' }
      ]);
    }
  }, [isOpen]);

  const getWelcomeMessage = () => {
    if (isComingSoon) {
      return 'Coming soon. This chatbot is not functional yet.';
    }
    return `Hello ${userName}! 👋\n\nI'm your Customer Service Assistant. I'm here to help you with:\n\n🔍 Client lookups and account information\n📋 Process guidance and procedures\n📞 Call scripts and common responses\n🎯 Escalation procedures\n📚 Knowledge base searches\n🎫 Ticket management\n\nHow can I assist you today?`;
  };

  const quickActions: QuickAction[] = [
    {
      id: 'client_lookup',
      label: 'Client Lookup',
      icon: <Search className="h-4 w-4" />,
      action: () => handleQuickAction('client_lookup'),
      category: 'lookup'
    },
    {
      id: 'dispute_process',
      label: 'Dispute Process',
      icon: <FileText className="h-4 w-4" />,
      action: () => handleQuickAction('dispute_process'),
      category: 'process'
    },
    {
      id: 'escalation',
      label: 'Escalation Guide',
      icon: <Users className="h-4 w-4" />,
      action: () => handleQuickAction('escalation'),
      category: 'escalation'
    },
    {
      id: 'call_scripts',
      label: 'Call Scripts',
      icon: <Phone className="h-4 w-4" />,
      action: () => handleQuickAction('call_scripts'),
      category: 'scripts'
    },
    {
      id: 'common_issues',
      label: 'Common Issues',
      icon: <HelpCircle className="h-4 w-4" />,
      action: () => handleQuickAction('common_issues'),
      category: 'knowledge'
    },
    {
      id: 'policy_lookup',
      label: 'Policy Lookup',
      icon: <Bookmark className="h-4 w-4" />,
      action: () => handleQuickAction('policy_lookup'),
      category: 'policy'
    }
  ];

  const handleQuickAction = (actionId: string) => {
    if (isComingSoon) return;
    let responseContent = '';
    let category = 'general';

    switch (actionId) {
      case 'client_lookup':
        responseContent = `**Client Lookup Process:**\n\n1. 🔍 **Search Methods:**\n   • Client ID (most accurate)\n   • Full name + DOB\n   • Phone number\n   • Email address\n   • SSN (last 4 digits)\n\n2. 📋 **Required Verification:**\n   • Name\n   • Date of birth\n   • Address\n   • Phone number\n\n3. ⚠️ **Privacy Notes:**\n   • Never share account details over unsecured channels\n   • Verify identity before providing information\n   • Log all lookup activities\n\nWould you like me to help you look up a specific client?`;
        category = 'client_lookup';
        break;

      case 'dispute_process':
        responseContent = `**Credit Dispute Process:**\n\n📝 **Initial Steps:**\n1. Gather client information\n2. Identify items to dispute\n3. Collect supporting documentation\n4. Choose dispute method (online/mail)\n\n⏱️ **Timeline:**\n• Investigation: 30-45 days\n• Follow-up: 15 days after initial period\n• Re-dispute: Available if unsatisfactory\n\n📋 **Required Documents:**\n• Government-issued ID\n• Proof of address\n• Supporting evidence for dispute\n\n🎯 **Success Tips:**\n• Be specific about inaccuracies\n• Provide clear documentation\n• Follow up regularly\n\nNeed help with a specific dispute?`;
        category = 'process_help';
        break;

      case 'escalation':
        responseContent = `**Escalation Guidelines:**\n\n🔴 **Immediate Escalation (Level 2):**\n• Threatening or abusive language\n• Legal threats or attorney involvement\n• Regulatory complaints (CFPB, BBB)\n• Account security breaches\n\n🟡 **Standard Escalation (Supervisor):**\n• Complex account issues\n• Policy exceptions required\n• Client dissatisfaction after 2+ interactions\n• Technical system issues\n\n📞 **Escalation Contacts:**\n• Supervisor: Ext. 1200\n• Manager: Ext. 1100\n• Legal: Ext. 1500\n• IT Support: Ext. 2000\n\n**Always log escalation reason and provide context!**`;
        category = 'escalation';
        break;

      case 'call_scripts':
        responseContent = `**Call Scripts & Responses:**\n\n📞 **Opening:**\n"Thank you for calling Regal Credit Solutions. This is ${userName}, how may I assist you today?"\n\n🔍 **Verification:**\n"For security purposes, may I please verify your full name and date of birth?"\n\n⏳ **Hold Script:**\n"I need to review your account briefly. May I place you on a brief hold for 1-2 minutes?"\n\n❌ **Unable to Help:**\n"I understand your concern. Let me connect you with my supervisor who can better assist with this request."\n\n✅ **Closing:**\n"Is there anything else I can help you with today? Thank you for choosing Regal Credit Solutions."\n\nNeed a script for a specific situation?`;
        category = 'scripts';
        break;

      case 'common_issues':
        responseContent = `**Common Customer Issues & Solutions:**\n\n🔐 **Account Access:**\n• Password reset: Guide through security questions\n• Locked account: Verify identity, unlock manually\n• Portal issues: Clear cache, try different browser\n\n📊 **Credit Report Questions:**\n• Why score dropped: Review recent changes\n• Dispute timeline: 30-45 day investigation\n• Credit monitoring: Explain daily/weekly updates\n\n💳 **Payment Issues:**\n• Failed payment: Check bank info, retry\n• Refund requests: Verify policy, process if eligible\n• Payment plans: Available for qualifying accounts\n\n📱 **Technical Support:**\n• App not working: Update app, restart device\n• Email not received: Check spam, update email\n• Slow loading: Clear cache, check internet\n\nWhat specific issue are you helping with?`;
        category = 'knowledge';
        break;

      case 'policy_lookup':
        responseContent = `**Key Policies & Procedures:**\n\n💰 **Refund Policy:**\n• Full refund: Within 7 days of service start\n• Partial refund: Case-by-case basis\n• No refund: After initial dispute filing\n\n🔄 **Service Changes:**\n• Upgrade: Anytime with price difference\n• Downgrade: At next billing cycle\n• Pause service: Up to 3 months\n\n📞 **Communication Policy:**\n• Response time: 24-48 hours\n• Preferred method: Client portal messages\n• Emergency contact: Phone during business hours\n\n🚫 **Service Limitations:**\n• No guaranteed score improvements\n• No removal of accurate information\n• No expedited dispute processing\n\nNeed details on a specific policy?`;
        category = 'policy';
        break;

      default:
        responseContent = "I'm here to help! Please select a topic or ask me a specific question.";
    }

    const assistantMessage: ChatMessage = {
      id: `quick_${Date.now()}`,
      role: 'assistant',
      content: responseContent,
      timestamp: new Date(),
      category: category
    };

    setMessages(prev => [...prev, assistantMessage]);
    setCurrentCategory(category);
  };

  const sendMessage = async () => {
    if (isComingSoon) return;
    if (!inputValue.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
      category: currentCategory
    };

    setMessages(prev => [...prev, userMessage]);
    setSearchHistory(prev => [inputValue.trim(), ...prev.slice(0, 9)]);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      // Simulate API call with customer service specific responses
      const response = await mockCustomerServiceResponse(userMessage.content, currentCategory);

      const assistantMessage: ChatMessage = {
        id: `assistant_${Date.now()}`,
        role: 'assistant',
        content: response.content,
        timestamp: new Date(),
        category: response.category || currentCategory,
        tags: response.tags
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Handle special actions
      if (response.action === 'client_lookup' && onClientLookup && response.clientId) {
        onClientLookup(response.clientId);
      } else if (response.action === 'escalation' && onEscalation) {
        onEscalation({ message: userMessage.content, category: currentCategory });
      }

    } catch (err) {
      console.error('Customer Service Chatbot Error:', err);
      setError('Failed to get response. Please try again.');

      const errorMessage: ChatMessage = {
        id: `error_${Date.now()}`,
        role: 'assistant',
        content: "I'm having trouble connecting right now. For immediate assistance, please contact your supervisor or call the support line.",
        timestamp: new Date(),
        category: 'error'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const mockCustomerServiceResponse = async (message: string, category: string) => {
    // Simulate processing delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    const lowerMessage = message.toLowerCase();

    // Client lookup detection
    if (lowerMessage.includes('client') && (lowerMessage.includes('lookup') || lowerMessage.includes('find'))) {
      return {
        content: `To look up a client, I need one of the following:\n\n🆔 **Client ID** (most reliable)\n👤 **Full Name + Date of Birth**\n📞 **Phone Number**\n📧 **Email Address**\n🔢 **Last 4 digits of SSN**\n\nPlease provide the information you have, and I'll guide you through the lookup process.\n\n⚠️ **Remember**: Always verify the client's identity before accessing their account information.`,
        category: 'client_lookup',
        tags: ['lookup', 'verification', 'security']
      };
    }

    // Escalation keywords
    if (lowerMessage.includes('escalate') || lowerMessage.includes('supervisor') || lowerMessage.includes('manager')) {
      return {
        content: `**Escalation Process:**\n\n1. 📝 **Document the Issue:**\n   • Client name and ID\n   • Nature of complaint\n   • Steps already taken\n   • Client's desired outcome\n\n2. 📞 **Contact Information:**\n   • Supervisor: Ext. 1200\n   • Manager: Ext. 1100\n   • After Hours: Use escalation form\n\n3. 🎫 **Create Escalation Ticket:**\n   • Priority level\n   • Time-sensitive issues\n   • Required follow-up\n\nWould you like me to help you document this escalation?`,
        category: 'escalation',
        action: 'escalation',
        tags: ['escalation', 'supervisor', 'documentation']
      };
    }

    // Policy questions
    if (lowerMessage.includes('policy') || lowerMessage.includes('refund') || lowerMessage.includes('cancel')) {
      return {
        content: `**Policy Information:**\n\n💰 **Refunds:**\n• Full refund: 7 days from service start\n• Partial refund: Manager approval required\n• Processing time: 5-7 business days\n\n🔄 **Cancellations:**\n• Client request: Effective immediately\n• No penalty: Within first 30 days\n• Final bill: Prorated to cancellation date\n\n📋 **Documentation Required:**\n• Written request (email or portal)\n• Account verification\n• Reason for cancellation\n\nWhich specific policy do you need clarification on?`,
        category: 'policy',
        tags: ['policy', 'refund', 'cancellation']
      };
    }

    // Default helpful response
    return {
      content: `I understand you're asking about: "${message}"\n\nHere are some ways I can help:\n\n🔍 **Client Services:**\n• Account lookups and verification\n• Service explanations\n• Process guidance\n\n📞 **Call Support:**\n• Script suggestions\n• Response templates\n• Escalation procedures\n\n📚 **Knowledge Base:**\n• Policy information\n• Common solutions\n• Best practices\n\nCould you provide more specific details about what you need help with?`,
      category: 'general',
      tags: ['general', 'assistance']
    };
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setCurrentCategory('general');
    setError(null);
    const welcomeMessage = getWelcomeMessage();
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: welcomeMessage,
      timestamp: new Date(),
      category: 'general'
    }]);
  };

  const bookmarkMessage = (message: ChatMessage) => {
    setBookmarkedResponses(prev => [...prev, message]);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getPositionClasses = () => {
    switch (position) {
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'top-right':
        return 'top-4 right-4';
      case 'top-left':
        return 'top-4 left-4';
      default:
        return 'bottom-4 right-4';
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) {
    return (
      <div className={`fixed ${getPositionClasses()} z-50`}>
        <div className="relative">
          {/* Notification badge for active tickets */}
          {activeTickets.length > 0 && (
            <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
              {activeTickets.length}
            </div>
          )}
          <button
            onClick={() => setIsOpen(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-105"
            title="Customer Service Assistant"
          >
            <MessageSquare className="h-6 w-6" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed ${getPositionClasses()} z-50`}>
      <div className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl transition-all duration-300 ${
        isMinimized ? 'w-80 h-16' : 'w-96 h-[600px]'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-purple-600 text-white rounded-t-lg">
          <div className="flex items-center space-x-2">
            <Bot className="h-5 w-5" />
            <div>
              <h3 className="font-medium">CS Assistant</h3>
              <p className="text-xs text-purple-200">{userDepartment}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="text-xs text-purple-200">
              Tickets: {activeTickets.length}
            </div>
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="text-white/80 hover:text-white p-1 rounded"
              title={isMinimized ? 'Maximize' : 'Minimize'}
            >
              {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
            </button>
            <button
              onClick={clearChat}
              className="text-white/80 hover:text-white p-1 rounded"
              title="Clear Chat"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white p-1 rounded"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {isComingSoon && (
              <div className="mx-4 mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300">
                Coming soon. This chatbot is not functional yet.
              </div>
            )}
            {/* Quick Actions */}
            {showQuickActions && !isComingSoon && (
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Quick Actions</span>
                  <button
                    onClick={() => setShowQuickActions(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {quickActions.slice(0, 6).map((action) => (
                    <button
                      key={action.id}
                      onClick={action.action}
                      className="flex flex-col items-center p-2 text-xs bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      title={action.label}
                    >
                      {action.icon}
                      <span className="mt-1 text-[10px] text-center leading-tight">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ height: showQuickActions ? '320px' : '400px' }}>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start space-x-3 ${
                    message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                  }`}
                >
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    message.role === 'user'
                      ? 'bg-purple-600 text-white'
                      : message.role === 'system'
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}>
                    {message.role === 'user' ? <User className="h-4 w-4" /> :
                     message.role === 'system' ? <Settings className="h-4 w-4" /> :
                     <Bot className="h-4 w-4" />}
                  </div>
                  <div className={`flex-1 max-w-xs ${
                    message.role === 'user' ? 'text-right' : 'text-left'
                  }`}>
                    <div className={`inline-block p-3 rounded-lg relative group ${
                      message.role === 'user'
                        ? 'bg-purple-600 text-white'
                        : message.role === 'system'
                        ? 'bg-orange-100 dark:bg-orange-900/20 text-orange-900 dark:text-orange-300'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>

                      {/* Message actions */}
                      {message.role === 'assistant' && (
                        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                          <button
                            onClick={() => copyToClipboard(message.content)}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                            title="Copy"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => bookmarkMessage(message)}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                            title="Bookmark"
                          >
                            <Bookmark className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatTime(message.timestamp)}
                      </p>
                      {message.category && message.category !== 'general' && (
                        <span className="text-xs bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded">
                          {message.category}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                    <Bot className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                  </div>
                  <div className="flex-1">
                    <div className="inline-block p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Loader className="h-4 w-4 animate-spin text-gray-600 dark:text-gray-300" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">Searching knowledge base...</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Error Display */}
            {error && (
              <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
                <div className="flex items-center space-x-2 text-red-700 dark:text-red-400">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Input */}
            <div className="border-t border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center space-x-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={isComingSoon ? 'Coming soon' : 'Ask about policies, procedures, clients...'}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  disabled={isLoading || isComingSoon}
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputValue.trim() || isLoading || isComingSoon}
                  className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white p-2 rounded-md transition-colors"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {isComingSoon
                  ? 'Coming soon. Chat interactions are disabled.'
                  : '💡 Try: "client lookup", "dispute process", "escalation", or "call script"'}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerServiceChatbot;